import { useEffect, useState } from 'react';
import type { RightsMap } from '@ehr/shared';
import { api } from './api.js';
import { useSession } from '../auth/session.js';

/**
 * The signed-in person's effective feature rights (GET /me/rights: their role's rights in Role
 * Rights plus their own Access Rights overrides; admins hold every right). Under View as it is the
 * viewed person's, as the API answers for them. Fetched once per person and shared by every
 * screen; saving rights fires RIGHTS_CHANGED_EVENT so open screens refetch.
 *
 * null until the answer arrives (and if it can't be read): a screen gated on a right keeps that
 * control hidden until then, the same answer the API gives a person without the right.
 */
export const RIGHTS_CHANGED_EVENT = 'credify:rights-changed';

const cache = new Map<string, RightsMap>();
const inflight = new Map<string, Promise<RightsMap | null>>();

function load(key: string, fresh = false): Promise<RightsMap | null> {
  const hit = cache.get(key);
  if (!fresh && hit) return Promise.resolve(hit);
  const running = inflight.get(key);
  if (!fresh && running) return running;
  const p = api
    .get<RightsMap>('/me/rights')
    .then((r) => {
      if (r) cache.set(key, r);
      return r ?? null;
    })
    .catch(() => cache.get(key) ?? null)
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

/** True when the rights map grants this right (false while unknown). */
export const granted = (rights: RightsMap | null | undefined, key: string): boolean =>
  rights?.[key] === true;

export function useMyRights(): RightsMap | null {
  const { user } = useSession();
  const key = user ? `${user.orgId}:${user.id}` : '';
  const [rights, setRights] = useState<RightsMap | null>(() =>
    key ? (cache.get(key) ?? null) : null,
  );
  useEffect(() => {
    if (!key) {
      setRights(null);
      return;
    }
    let alive = true;
    setRights(cache.get(key) ?? null);
    void load(key).then((r) => alive && setRights(r));
    const onChange = () => void load(key, true).then((r) => alive && setRights(r));
    window.addEventListener(RIGHTS_CHANGED_EVENT, onChange);
    return () => {
      alive = false;
      window.removeEventListener(RIGHTS_CHANGED_EVENT, onChange);
    };
  }, [key]);
  return rights;
}
